import React, { useState } from 'react';
import { Clear } from '@mui/icons-material';
import { CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import Error from 'components/Error/Error';
import { filterConcepts } from 'components/ConceptManager/util';
import { ComputerIcon, StoreIcon } from 'components/Icons';
import { useGetApplicationBlockedCategoriesQuery } from 'reduxState/store/concept/api';
import { Concept } from 'reduxState/store/concept/types';

interface BlockedCategoriesProps {
  applicationId: string;
}

interface RowData {
  concepts: Concept[];
}

const ConceptRow = ({ index, style, data }: ListChildComponentProps<RowData>): JSX.Element => {
  const concept = data.concepts[index];
  return (
    <li style={style} className={`flex items-center gap-2 px-3 py-1 ${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'}`}>
      {concept.kind === 'merchant' ? (
        <span className="cursor-help shrink-0" title="Merchant" aria-label="Merchant">
          <StoreIcon className="w-5 h-5 text-wildfire-orange" />
        </span>
      ) : concept.kind === 'domain' ? (
        <span className="cursor-help shrink-0" title="Domain" aria-label="Domain">
          <ComputerIcon className="w-5 h-5 text-wildfire-purple" />
        </span>
      ) : null}
      <span className="text-sm">{concept.value}</span>
      <span className="text-xs text-gray-400 ml-auto">{concept.kind}</span>
    </li>
  );
};

const BlockedCategories = ({ applicationId }: BlockedCategoriesProps): JSX.Element => {
  const [filter, setFilter] = useState('');
  const { data, isLoading, isError, refetch } = useGetApplicationBlockedCategoriesQuery(applicationId);

  if (isError) {
    return <Error retry={refetch} />;
  }

  const concepts = data ?? [];
  const filtered = filterConcepts(concepts, filter);

  return (
    <div className="flex flex-1 flex-col">
      <h1 className="text-muted-dark-purple">Blocked Categories</h1>
      <div className="bg-white p-5 rounded-md flex flex-1 flex-col min-h-[50vh]">
        <div className="flex items-center justify-between mb-3">
          <Typography variant="h6">
            Blocked Categories{!isLoading && ` (${filtered.length}/${concepts.length})`}
          </Typography>
        </div>
        <TextField
          label="Filter"
          variant="standard"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton aria-label="clear filter" size="small" edge="end" onClick={() => setFilter('')}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          className="mb-3"
        />
        {isLoading ? (
          <div className="flex justify-center mt-10">
            <CircularProgress />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center mt-10">
            <Typography color="textSecondary">
              {filter ? 'No categories match your filter.' : 'No categories are currently blocked.'}
            </Typography>
          </div>
        ) : (
          <div className="flex-auto overflow-hidden">
            <AutoSizer>
              {/* @ts-expect-error: AutoSizer render prop types are not correctly inferred */}
              {({ height, width }): JSX.Element => (
                <FixedSizeList
                  height={height}
                  width={width}
                  itemSize={32}
                  itemCount={filtered.length}
                  itemData={{ concepts: filtered }}
                  innerElementType="ul"
                >
                  {ConceptRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockedCategories;
